'use client';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useGetStripeConnection } from '@/features/integrations/api/get-stripe-connection';
import {
  type UpdateOrganizationIntent,
  useActiveOrganization,
  useUpdateOrganization,
} from '@/features/organization';
import {
  updateOrganizationFields,
  updateOrganizationForm,
} from '@/features/organization/api/update-organization/update-organization.form';
import { micrositeBookingUrl } from '@/lib/microsite-url';
import { ROUTES } from '@/lib/route-paths';
import { cn } from '@/lib/utils';
import {
  depositBasisLabels,
  depositBasisValues,
} from '@borradh-workspace/api-client/types';
import {
  BOOKING_MAX_DAYS_AHEAD_DEFAULT,
  BOOKING_MAX_DAYS_AHEAD_LIMIT,
  BOOKING_MIN_DAYS_AHEAD_DEFAULT,
  BOOKING_MIN_DAYS_AHEAD_LIMIT,
} from '@borradh-workspace/labels';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarDays, ExternalLink, Link2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { DaysAheadField, bookingWindowSummary } from './booking-window-fields';

const bookingModes = ['borradh', 'booking_link'] as const;
type BookingMode = (typeof bookingModes)[number];

// Booking destination, deposits and (new booking system only) the booking
// window share this form. Cancellation and rescheduling settings are saved
// independently by their own tab.
const F = updateOrganizationFields;

const bookingsBaseSchema = z.object({
  bookingMode: z.enum(bookingModes),
  defaultBookingLink: F.defaultBookingLink.schema,
  depositEnabled: F.depositEnabled.schema,
  depositAmount: F.depositAmount.schema,
  defaultDepositBasis: F.defaultDepositBasis.schema,
  defaultDepositPercent: F.defaultDepositPercent.schema,
  bookingMinDaysAhead: F.bookingMinDaysAhead.schema,
  bookingMaxDaysAhead: F.bookingMaxDaysAhead.schema,
});

const bookingModeSchema = bookingsBaseSchema
  .refine(
    (data) => {
      if (data.bookingMode === 'booking_link') {
        return !!data.defaultBookingLink;
      }
      return true;
    },
    {
      message: 'Booking link is required when using an external booking link',
      path: ['defaultBookingLink'],
    }
  )
  .refine((data) => data.bookingMaxDaysAhead >= data.bookingMinDaysAhead, {
    message: 'Max days ahead cannot be less than min days ahead',
    path: ['bookingMaxDaysAhead'],
  });

const bookingsSchema = bookingModeSchema
  .refine(
    (data) => {
      // Only the FIXED basis has an amount to require. `depositAmount` renders
      // solely under `defaultDepositBasis === 'fixed'`, so requiring it for a
      // percent deposit failed validation on a field that is not on screen —
      // zodResolver rejected, no error could be shown anywhere, and Save
      // silently did nothing for any clinic charging a percentage.
      if (data.depositEnabled && data.defaultDepositBasis === 'fixed') {
        return !!data.depositAmount && data.depositAmount > 0;
      }
      return true;
    },
    {
      message: 'Enter a deposit amount greater than 0',
      path: ['depositAmount'],
    }
  )
  .refine(
    (data) => {
      // The percent basis has its own required field, held to the 1–100 the
      // input already advertises.
      if (data.depositEnabled && data.defaultDepositBasis === 'percent') {
        return (
          !!data.defaultDepositPercent &&
          data.defaultDepositPercent > 0 &&
          data.defaultDepositPercent <= 100
        );
      }
      return true;
    },
    {
      message: 'Enter a deposit percentage between 1 and 100',
      path: ['defaultDepositPercent'],
    }
  );

type BookingsFormValues = z.infer<typeof bookingsBaseSchema>;

function bookingWindowValues(
  org:
    | {
        bookingMinDaysAhead?: number | null;
        bookingMaxDaysAhead?: number | null;
      }
    | null
    | undefined
) {
  return {
    bookingMinDaysAhead:
      org?.bookingMinDaysAhead ?? BOOKING_MIN_DAYS_AHEAD_DEFAULT,
    bookingMaxDaysAhead:
      org?.bookingMaxDaysAhead ?? BOOKING_MAX_DAYS_AHEAD_DEFAULT,
  };
}

/**
 * Derive the booking mode from primaryCalendarType.
 * 'borradh' calendar → use Borradh booking page
 * Anything else (or null) → use external booking link
 */
function deriveBookingMode(org: {
  bookingDestination?: string | null;
  primaryCalendarType?: string | null;
  defaultBookingLink?: string | null;
}): BookingMode {
  // `bookingDestination` is the field of record (ENG-500). The legacy
  // primaryCalendarType fallback stays only until PR 2 drops that column, so
  // an org loaded before the migration still resolves to the right mode.
  if (org.bookingDestination) {
    return org.bookingDestination === 'borradh' ? 'borradh' : 'booking_link';
  }
  if (org.primaryCalendarType === 'borradh') return 'borradh';
  if (org.defaultBookingLink) return 'booking_link';
  return 'borradh';
}

export function BookingsTab({
  className,
  onCancel,
  ...props
}: React.ComponentProps<'form'> & { onCancel?: () => void }) {
  const L = updateOrganizationForm.labels;
  const {
    data: orgData,
    isPending: isOrganizationLoading,
    error: organizationError,
  } = useActiveOrganization();

  const organizationData = orgData;
  const {
    isConnected,
    chargesEnabled,
    isLoading: isStripeLoading,
    isError: isStripeError,
  } = useGetStripeConnection();
  const canCollectDeposits =
    !isStripeLoading && !isStripeError && isConnected && chargesEnabled;

  const { execute: updateOrganization, isExecuting: isUpdating } =
    useUpdateOrganization();

  const [pendingPolicy, setPendingPolicy] =
    useState<UpdateOrganizationIntent | null>(null);
  const [applyToAllServices, setApplyToAllServices] = useState(false);

  const form = useForm<BookingsFormValues>({
    // Disabled deposit fields are preserved, not submitted. Do not let a
    // legacy incomplete policy block unrelated booking settings after Stripe
    // disconnects; paid edits still use the full validation below.
    resolver: zodResolver(
      canCollectDeposits ? bookingsSchema : bookingModeSchema
    ),
    defaultValues: {
      bookingMode: organizationData
        ? deriveBookingMode(organizationData)
        : 'borradh',
      defaultBookingLink: organizationData?.defaultBookingLink ?? '',
      depositEnabled: organizationData?.depositEnabled ?? false,
      defaultDepositBasis: organizationData?.defaultDepositBasis ?? 'fixed',
      defaultDepositPercent: organizationData?.defaultDepositPercent ?? 20,
      depositAmount: organizationData?.depositAmount
        ? organizationData.depositAmount / 100
        : undefined,
      ...bookingWindowValues(organizationData),
    },
  });

  const selectedMode = form.watch('bookingMode');
  const depositEnabled = form.watch('depositEnabled');
  const depositBasis = form.watch('defaultDepositBasis');
  const minDaysAhead = form.watch('bookingMinDaysAhead');
  const maxDaysAhead = form.watch('bookingMaxDaysAhead');
  const windowSummary = bookingWindowSummary(minDaysAhead, maxDaysAhead);

  // Settled edits keep the pair consistent: pushing min past max carries max
  // along, and pulling max under min carries min down. Raw keystrokes do not,
  // or typing "14" into max would drag min to 1 on the way.
  const commitMinDays = (days: number) => {
    form.setValue('bookingMinDaysAhead', days, { shouldDirty: true });
    if (days > form.getValues('bookingMaxDaysAhead')) {
      form.setValue('bookingMaxDaysAhead', Math.max(days, 1), {
        shouldDirty: true,
      });
    }
    void form.trigger(['bookingMinDaysAhead', 'bookingMaxDaysAhead']);
  };
  const commitMaxDays = (days: number) => {
    form.setValue('bookingMaxDaysAhead', days, { shouldDirty: true });
    if (days < form.getValues('bookingMinDaysAhead')) {
      form.setValue('bookingMinDaysAhead', days, { shouldDirty: true });
    }
    void form.trigger(['bookingMinDaysAhead', 'bookingMaxDaysAhead']);
  };

  const orgSlug = orgData?.slug ?? null;
  const handleCopyCalendarLink = async () => {
    if (!orgSlug) return;
    // The canonical customer url on the marketing host — not the retired
    // {dashboard}/book/{slug}, which only resolves via an edge redirect that
    // does not exist off app.borradh.io. This one gets pasted into bios.
    const url = micrositeBookingUrl(orgSlug);
    try {
      await navigator.clipboard.writeText(url);
      toast('Calendar link copied', {
        description: 'Share this so clients can book with you directly.',
      });
    } catch {
      toast.error("Couldn't copy the link — try again.");
    }
  };

  useEffect(() => {
    if (organizationData) {
      form.reset({
        bookingMode: deriveBookingMode(organizationData),
        defaultBookingLink: organizationData.defaultBookingLink ?? '',
        depositEnabled: organizationData.depositEnabled ?? false,
        defaultDepositBasis: organizationData?.defaultDepositBasis ?? 'fixed',
        defaultDepositPercent: organizationData?.defaultDepositPercent ?? 20,
        depositAmount: organizationData.depositAmount
          ? organizationData.depositAmount / 100
          : undefined,
        ...bookingWindowValues(organizationData),
      });
    }
  }, [organizationData, form]);

  async function onSubmit(values: BookingsFormValues) {
    // Derive the wire fields from the booking-mode UI, then hand them to the
    // shared builder as intent. Only the keys set here are patched.
    const payload: UpdateOrganizationIntent = {};

    switch (values.bookingMode) {
      case 'borradh':
        // Write the field of record; the service keeps the legacy column in
        // step while both exist (ENG-500).
        payload.bookingDestination = 'borradh';
        payload.defaultBookingLink = null;
        break;
      case 'booking_link':
        payload.bookingDestination = 'external_link';
        payload.defaultBookingLink = values.defaultBookingLink || null;
        break;
    }

    // Per-clinic deposit (stored in cents). Disabled or 0 → no payment step.
    // Preserve a saved policy when Stripe becomes unavailable. Other booking
    // settings can still be saved, and the deposit switch can still be turned
    // off. The API independently checks Stripe before accepting paid settings.
    if (canCollectDeposits || !values.depositEnabled) {
      payload.depositEnabled = values.depositEnabled;
      // Basis and percentage are only sent while deposits are ON — with them off
      // there is no deposit for a basis to describe, and patching them would
      // rewrite settings the user never opened.
      if (values.depositEnabled) {
        payload.defaultDepositBasis = values.defaultDepositBasis;
        // Sent as entered even when the basis is `fixed`. resolveBookingPayment
        // reads only the one the basis selects, so the other is inert — and
        // keeping it means switching basis back doesn't wipe the typed figure.
        payload.defaultDepositPercent = values.defaultDepositPercent ?? null;
      }
      if (
        canCollectDeposits ||
        organizationData?.defaultPaymentPolicy !== 'full'
      ) {
        payload.depositAmount =
          values.depositEnabled && values.depositAmount
            ? Math.round(values.depositAmount * 100)
            : 0;
      }
    }

    if (values.bookingMode === 'borradh') {
      payload.bookingMinDaysAhead = values.bookingMinDaysAhead;
      payload.bookingMaxDaysAhead = values.bookingMaxDaysAhead;
    }

    const depositsEnabled =
      payload.depositEnabled ?? organizationData?.depositEnabled ?? false;
    const policyChanged =
      organizationData &&
      ((payload.depositEnabled !== undefined &&
        payload.depositEnabled !== organizationData.depositEnabled) ||
        (depositsEnabled &&
          ((payload.defaultDepositBasis !== undefined &&
            payload.defaultDepositBasis !==
              organizationData.defaultDepositBasis) ||
            (payload.depositAmount !== undefined &&
              (payload.depositAmount ?? 0) !==
                (organizationData.depositAmount ?? 0)) ||
            (payload.defaultDepositPercent !== undefined &&
              payload.defaultDepositPercent !==
                organizationData.defaultDepositPercent))));
    if (policyChanged) {
      setApplyToAllServices(false);
      setPendingPolicy(payload);
      return;
    }
    await updateOrganization(payload);
  }

  if (isOrganizationLoading) {
    return (
      <div className={cn('flex flex-col px-6 py-4 gap-6', className)}>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (organizationError) {
    return (
      <div className={cn('flex flex-col px-6 py-4 gap-6', className)}>
        <div className="text-destructive">
          Error loading organization data: {organizationError.message}
        </div>
      </div>
    );
  }

  return (
    <form
      className={cn('flex flex-col px-6 py-4', className)}
      onSubmit={form.handleSubmit(onSubmit)}
      {...props}
    >
      <AlertDialog
        open={pendingPolicy !== null}
        onOpenChange={(open) => {
          if (!open && !isUpdating) setPendingPolicy(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply this deposit policy to existing services?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Choose which services use your new default. Deposits are capped at
              each service’s price. You can edit the deposit on any service
              afterward. Existing bookings keep their agreed payment terms.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <RadioGroup
            aria-label="Apply deposit policy"
            value={applyToAllServices ? 'all' : 'new'}
            onValueChange={(value) => setApplyToAllServices(value === 'all')}
            disabled={isUpdating}
          >
            <FieldLabel>
              <Field orientation="horizontal">
                <RadioGroupItem value="new" />
                <FieldContent>
                  <FieldTitle>New services only</FieldTitle>
                  <FieldDescription>
                    Keep existing services’ deposit settings.
                  </FieldDescription>
                </FieldContent>
              </Field>
            </FieldLabel>
            <FieldLabel>
              <Field orientation="horizontal">
                <RadioGroupItem value="all" />
                <FieldContent>
                  <FieldTitle>All services</FieldTitle>
                  <FieldDescription>
                    Replace the deposit settings on all existing services.
                  </FieldDescription>
                </FieldContent>
              </Field>
            </FieldLabel>
          </RadioGroup>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              disabled={isUpdating}
              onClick={async () => {
                if (!pendingPolicy) return;
                try {
                  await updateOrganization({
                    ...pendingPolicy,
                    applyDepositPolicyToAllServices: applyToAllServices,
                  });
                  setPendingPolicy(null);
                } catch {
                  /* The mutation displays the error; keep the choice available to retry. */
                }
              }}
            >
              {isUpdating ? 'Saving...' : 'Save policy'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Controller
        name="bookingMode"
        control={form.control}
        render={({ field }) => (
          <FieldSet>
            <FieldLegend>{L.bookingDestination}</FieldLegend>
            <FieldDescription className="-mt-2 mb-1">
              Choose how clients book appointments with your organization.
            </FieldDescription>
            <RadioGroup
              aria-label={L.bookingDestination}
              value={field.value}
              onValueChange={field.onChange}
            >
              {/* Borradh Calendar */}
              <FieldLabel>
                <Field orientation="horizontal">
                  <RadioGroupItem value="borradh" />
                  <FieldContent>
                    <FieldTitle>
                      <CalendarDays className="size-4" />
                      Borrah Calendar
                    </FieldTitle>
                    <FieldDescription>
                      Use the built-in calendar and booking system.
                    </FieldDescription>
                  </FieldContent>
                </Field>
              </FieldLabel>

              {/* Booking Link */}
              <FieldLabel>
                <Field orientation="horizontal">
                  <RadioGroupItem value="booking_link" />
                  <FieldContent>
                    <FieldTitle>
                      <ExternalLink className="size-4" />
                      Booking Link
                    </FieldTitle>
                    <FieldDescription>
                      Use an external URL where clients can book directly.
                    </FieldDescription>
                  </FieldContent>
                </Field>
              </FieldLabel>
            </RadioGroup>
          </FieldSet>
        )}
      />

      {/* Calendar link (shown only for the built-in Borradh calendar) */}
      {selectedMode === 'borradh' && (
        <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
          <div className="min-w-0">
            <p className="font-medium text-sm">Your calendar link</p>
            <p className="truncate text-muted-foreground text-sm">
              Share this so clients can book with you directly.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopyCalendarLink}
            disabled={!orgSlug}
            aria-label="Copy your booking calendar link"
            className="shrink-0"
          >
            <Link2 className="size-4" />
            Copy calendar link
          </Button>
        </div>
      )}

      {/* Booking link URL input (shown only when booking_link is selected) */}
      {selectedMode === 'booking_link' && (
        <div className="mt-4">
          <Controller
            name="defaultBookingLink"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name} className="font-medium">
                  {L.defaultBookingLink}
                </FieldLabel>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  id={field.name}
                  type="url"
                  placeholder="https://booking.example.com"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        </div>
      )}

      {/* Deposit settings — only relevant for the built-in Borradh flow. */}
      {selectedMode === 'borradh' && (
        <>
          <Separator className="my-4" />

          <Controller
            name="depositEnabled"
            control={form.control}
            render={({ field }) => (
              <Field
                orientation="horizontal"
                data-disabled={!canCollectDeposits && !field.value}
              >
                <FieldContent>
                  <FieldTitle>{L.depositEnabled}</FieldTitle>
                  <FieldDescription>
                    Clients pay a deposit via card to confirm their booking.
                    Requires a connected Stripe account.
                  </FieldDescription>
                </FieldContent>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!canCollectDeposits && !field.value}
                  aria-label={L.depositEnabled}
                />
              </Field>
            )}
          />

          {!canCollectDeposits && (
            <FieldDescription>
              {isStripeLoading
                ? 'Checking Stripe connection…'
                : isStripeError
                  ? 'Unable to check your Stripe connection. Try again before enabling deposits.'
                  : 'Connect a Stripe account that can accept payments to enable or change deposits.'}{' '}
              {!isStripeLoading && (
                <a href="/dashboard/settings/payments">Connect Stripe</a>
              )}
            </FieldDescription>
          )}

          {depositEnabled && (
            <FieldSet
              disabled={!canCollectDeposits}
              className="mt-4 flex flex-col gap-4"
            >
              <Controller
                name="defaultDepositBasis"
                control={form.control}
                render={({ field }) => (
                  <Field>
                    <FieldLabel htmlFor={field.name} className="font-medium">
                      {L.defaultDepositBasis}
                    </FieldLabel>
                    <FieldDescription>
                      A percentage scales with each service's price; a fixed
                      amount is the same for every booking.
                    </FieldDescription>
                    <Select
                      disabled={!canCollectDeposits}
                      value={field.value}
                      onValueChange={(v) =>
                        field.onChange(v as 'fixed' | 'percent')
                      }
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {depositBasisValues.map((value) => (
                          <SelectItem key={value} value={value}>
                            {depositBasisLabels[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              />

              {depositBasis === 'percent' && (
                <Controller
                  name="defaultDepositPercent"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={field.name} className="font-medium">
                        {L.defaultDepositPercent}
                      </FieldLabel>
                      <FieldDescription>
                        Share of the service price, rounded up to the nearest
                        50c. Services priced "on consultation" fall back to no
                        deposit.
                      </FieldDescription>
                      <InputGroup>
                        <InputGroupInput
                          id={field.name}
                          type="number"
                          min={1}
                          max={100}
                          step="1"
                          inputMode="numeric"
                          placeholder="20"
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? undefined
                                : e.target.valueAsNumber
                            )
                          }
                          onBlur={field.onBlur}
                          aria-invalid={fieldState.invalid}
                        />
                        <InputGroupAddon className="bg-muted text-foreground">
                          %
                        </InputGroupAddon>
                      </InputGroup>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              )}

              {depositBasis === 'fixed' && (
                <Controller
                  name="depositAmount"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={field.name} className="font-medium">
                        {L.depositAmount}
                      </FieldLabel>
                      <FieldDescription>
                        Amount charged per booking (in your account currency).
                      </FieldDescription>
                      <Input
                        id={field.name}
                        type="number"
                        min={0}
                        step="0.01"
                        inputMode="decimal"
                        placeholder="25.00"
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ''
                              ? undefined
                              : e.target.valueAsNumber
                          )
                        }
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              )}
            </FieldSet>
          )}
        </>
      )}

      {selectedMode === 'borradh' && (
        <>
          <Separator className="my-4" />
          <FieldSet>
            <FieldLegend>Booking window</FieldLegend>
            <FieldDescription className="-mt-2 mb-1">
              Choose how soon and how far ahead customers can book online. Your
              team can still book any date from the calendar.
            </FieldDescription>
            <Controller
              name="bookingMinDaysAhead"
              control={form.control}
              render={({ field, fieldState }) => (
                <DaysAheadField
                  id={field.name}
                  label={L.bookingMinDaysAhead}
                  description="The earliest day customers can book. 0 allows same-day bookings."
                  value={field.value}
                  min={0}
                  max={BOOKING_MIN_DAYS_AHEAD_LIMIT}
                  error={fieldState.error}
                  onChange={field.onChange}
                  onCommit={commitMinDays}
                  onBlur={field.onBlur}
                />
              )}
            />
            <Controller
              name="bookingMaxDaysAhead"
              control={form.control}
              render={({ field, fieldState }) => (
                <DaysAheadField
                  id={field.name}
                  label={L.bookingMaxDaysAhead}
                  description="The furthest day ahead customers can book."
                  value={field.value}
                  min={1}
                  max={BOOKING_MAX_DAYS_AHEAD_LIMIT}
                  error={fieldState.error}
                  onChange={field.onChange}
                  onCommit={commitMaxDays}
                  onBlur={field.onBlur}
                />
              )}
            />
            {windowSummary && (
              <p className="rounded-lg bg-muted/60 px-3 py-2 text-muted-foreground text-sm">
                {windowSummary}
              </p>
            )}
          </FieldSet>
        </>
      )}

      {selectedMode === 'borradh' && (
        <div className="mt-6">
          {organizationData?.bookingDestination === 'borradh' ? (
            <Button
              asChild
              variant="outline"
              className="h-auto max-w-full whitespace-normal text-left"
            >
              <a href={ROUTES.settingsCancellations}>
                Cancellation and rescheduling
                <ExternalLink className="size-4 shrink-0" aria-hidden="true" />
              </a>
            </Button>
          ) : (
            <p className="text-muted-foreground text-sm">
              Save Booking settings to enable cancellation and rescheduling.
            </p>
          )}
        </div>
      )}

      <Separator className="my-4" />

      <div className="flex gap-2">
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Submit'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
